import React from "react";
import Commonbadge from "Components/Common/Commonbadge";
import { formatDate } from "common/service/CommonService";

export const formatTime24Hour = (timeStr?: string | null) => {
  if (!timeStr) return "-";
  if (!timeStr.includes("-") && !timeStr.includes("/") && timeStr.includes(":")) {
    const parts = timeStr.split(":");
    if (parts.length >= 2) {
      return `${parts[0].trim()}:${parts[1].trim()}`;
    }
    return timeStr;
  }
  try {
    const date = new Date(timeStr);
    if (!isNaN(date.getTime())) {
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  } catch (e) {
    // Fallback
  }
  return timeStr;
};

export const renderDeviationBadge = (row: any) => {
  const devMins = row.deviationminutes;
  const devStatus = row.deviationstatus;
  const collTime = row.samplecollectiontime;

  let text = "Pending";
  let style: React.CSSProperties = {
    backgroundColor: "#f1f5f9",
    color: "#475569",
  };

  if (devStatus === "missed") {
    text = "Missed";
    style = {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
    };
  } else if (devMins !== null && devMins !== undefined) {
    if (devMins === 0) {
      text = "No Deviation";
      style = {
        backgroundColor: "#d1fae5",
        color: "#065f46",
      };
    } else {
      text = `${devMins > 0 ? "+" : ""}${devMins} min`;
      style = {
        backgroundColor: "#fef3c7",
        color: "#d97706",
      };
    }
  } else if (devStatus) {
    text = devStatus;
    const lower = devStatus.toLowerCase();
    if (lower === "normal" || lower === "no deviation") {
      style = {
        backgroundColor: "#d1fae5",
        color: "#065f46",
      };
    } else if (lower === "pending") {
      style = {
        backgroundColor: "#f1f5f9",
        color: "#475569",
      };
    } else {
      style = {
        backgroundColor: "#fef3c7",
        color: "#d97706",
      };
    }
  } else {
    if (!collTime) {
      text = "Pending";
      style = {
        backgroundColor: "#f1f5f9",
        color: "#475569",
      };
    } else {
      text = "No Deviation";
      style = {
        backgroundColor: "#d1fae5",
        color: "#065f46",
      };
    }
  }

  return (
    <Commonbadge
      status={text}
      style={{
        ...style,
        minWidth: "90px",
        textAlign: "center",
      }}
    />
  );
};

export const getSessionLogColumns = (activeTab: string, ac: any) => {
  return activeTab === "subjectToVacutainer" ? [
    {
      key: "subjectno",
      label: "Subject No",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.subjectno || "-"}
        </div>
      ),
    },
    {
      key: "subjectbarcode",
      label: "Subject",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`fw-600 ${ac.cellText}`}>
          {row.subjectbarcode || "-"}
        </div>
      ),
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.vaccutainerbarcode || "-"}
        </div>
      ),
    },
    {
      key: "dosingscheduledate",
      label: "Schedule Date",
      textCenter: true,
      render: (_: any, row?: any) =>
        row?.dosingscheduledate ? (
          <div className={`${ac.cellText}`}>
            {formatDate(row.dosingscheduledate)}
          </div>
        ) : <span className="text-muted">-</span>,
    },
    {
      key: "dosingscheduletime",
      label: "Schedule Time",
      textCenter: true,
      render: (_: any, row?: any) =>
        row?.dosingscheduletime ? (
          <div className={`${ac.cellText}`}>
            {formatTime24Hour(row.dosingscheduletime)}
          </div>
        ) : <span className="text-muted">-</span>,
    },
    {
      key: "samplecollectiontime",
      label: "Sample Collection Time",
      textCenter: true,
      render: (_: any, row?: any) =>
        row?.samplecollectiontime ? (
          <div className={`${ac.cellText}`}>
            {formatTime24Hour(row.samplecollectiontime)}
          </div>
        ) : <span className="text-muted">-</span>,
    },
    {
      key: "deviation",
      label: "Deviation",
      textCenter: true,
      render: (_: any, row: any) => renderDeviationBadge(row),
    },
    {
      key: "deviationstatus",
      label: "Deviation Status",
      textCenter: true,
      render: (_: any, row: any) => {
        const devStatus = row.deviationstatus;
        if (!devStatus) return <span className="text-muted">-</span>;

        const lower = String(devStatus).trim().toLowerCase();
        let badgeColor = "secondary";

        if (lower.includes("exceed") || lower.includes("missed") || lower.includes("error")) {
          badgeColor = "danger";
        } else if (lower.includes("early")) {
          badgeColor = "warning";
        } else if (lower.includes("within") || lower.includes("normal") || lower.includes("no deviation")) {
          badgeColor = "success";
        }

        return (
          <span
            className={`badge bg-${badgeColor} bg-opacity-10 text-${badgeColor} border border-${badgeColor} border-opacity-25 px-2 py-1 fw-semibold`}
            style={{ fontSize: "11px", minWidth: "110px", display: "inline-block", textAlign: "center" }}
          >
            {devStatus}
          </span>
        );
      },
    },
    {
      key: "deviationreason",
      label: "Deviation Reason",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.deviationreason || <span className="text-muted">-</span>}
        </div>
      ),
    },
    {
      key: "dosingstationno",
      label: "Dosing Station No",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.dosingstationno || "-"}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      textCenter: true,
      render: (_: any, row: any) => {
        const resStr = String(row.result || "").toLowerCase();
        if (resStr === "skipped") {
          return (
            <span
              className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold"
              style={{
                minWidth: "90px",
                display: "inline-block",
                backgroundColor: "#fff8e6",
                color: "#b45309",
                borderColor: "#f59e0b",
              }}
            >
              SKIPPED
            </span>
          );
        }
        const isValid = resStr === "valid";
        return (
          <span
            className={`badge ${isValid ? "bg-success" : "bg-danger"} bg-opacity-10 ${isValid ? "text-success" : "text-danger"} border ${isValid ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 text-uppercase`}
            style={{ minWidth: "90px", display: "inline-block" }}
          >
            <i
              className={`${isValid ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}
            ></i>{" "}
            {row.result}
          </span>
        );
      },
    },
    {
      key: "result",
      label: "Result",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`text-muted ${ac.cellText}`}>
          {row.resultmessage || "-"}
        </div>
      ),
    },
  ] : [
    {
      key: "scandate",
      label: "Date",
      textCenter: true,
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatDate(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "scantime",
      label: "Time",
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatTime24Hour(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`fw-600 ${ac.cellText}`}>
          {row.vaccutainerbarcode || "-"}
        </div>
      ),
    },
    {
      key: "aliquotbarcode",
      label: "Aliquot",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`${ac.cellText}`}>
          {row.aliquotbarcode || "-"}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      textCenter: true,
      render: (_: any, row: any) => {
        const resStr = String(row.result || "").toLowerCase();
        if (resStr === "skipped") {
          return (
            <span
              className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold"
              style={{
                minWidth: "90px",
                display: "inline-block",
                backgroundColor: "#fff8e6",
                color: "#b45309",
                borderColor: "#f59e0b",
              }}
            >
              SKIPPED
            </span>
          );
        }
        const isValid = resStr === "valid";
        return (
          <span
            className={`badge ${isValid ? "bg-success" : "bg-danger"} bg-opacity-10 ${isValid ? "text-success" : "text-danger"} border ${isValid ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 text-uppercase`}
            style={{ minWidth: "90px", display: "inline-block" }}
          >
            <i
              className={`${isValid ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}
            ></i>{" "}
            {row.result}
          </span>
        );
      },
    },
    {
      key: "result",
      label: "Result",
      textCenter: true,
      render: (_: any, row: any) => (
        <div className={`text-muted ${ac.cellText}`}>
          {row.resultmessage || "-"}
        </div>
      ),
    },
  ];
};
